/**
 * <quick-jump-menu> — floating pill listing the page's section headings.
 * Ported from the old theme's section-quick-jump: markup now rendered by
 * Liquid, headings collected via a configurable selector (data-selector),
 * scroll offset from the theme's --header-height variable.
 */
(function () {
  'use strict';

  function headerOffset() {
    return (parseInt(getComputedStyle(document.documentElement).getPropertyValue('--header-height'), 10) || 0);
  }

  class QuickJumpMenu extends HTMLElement {
    connectedCallback() {
      if (this.__init) return;
      this.__init = true;

      // #main-content carries a clip-path (footer reveal) that clips fixed
      // descendants, cutting the pill off near the footer — so the element
      // moves itself to <body>, like the old theme's JS-injected menu.
      // Remember the owning section for theme-editor cleanup first.
      var sectionEl = this.closest('.shopify-section');
      this.__sectionId = sectionEl ? sectionEl.id : null;
      if (this.parentElement !== document.body) {
        var self = this;
        document.querySelectorAll('body > quick-jump-menu').forEach(function (el) {
          if (el !== self) el.remove();
        });
        document.body.appendChild(this);
      }
      if (this.__sectionId) {
        var qj = this;
        document.addEventListener('shopify:section:unload', function (e) {
          if (e.target && e.target.id === qj.__sectionId) qj.remove();
        });
      }

      this.menu = this.querySelector('.quick-jump-menu');
      this.toggle = this.querySelector('.quick-jump-toggle');
      this.content = this.querySelector('.quick-jump-content');
      this.nav = this.querySelector('.quick-jump-nav');
      if (!this.menu || !this.toggle || !this.nav) return;

      this.threshold = parseInt(this.dataset.threshold, 10) || 150;
      this.collectHeadings(this.dataset.selector || 'h2[id^="section-heading-"]');
      if (!this.headings.length) return; // nothing to jump to — stay hidden

      this.visible = false;
      window.addEventListener('scroll', this.onScroll.bind(this), { passive: true });
      this.onScroll();
      this.setupEvents();
    }

    collectHeadings(selector) {
      var nav = this.nav;
      this.headings = [];
      var seen = {};
      document.querySelectorAll(selector).forEach(function (heading) {
        if (!heading.id) {
          var id = 'heading-' + heading.textContent.trim().toLowerCase()
            .replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
          while (seen[id] || document.getElementById(id)) id += '-2';
          heading.id = id;
        }
        seen[heading.id] = true;
        var text = heading.textContent.trim();
        if (!text) return;
        var link = document.createElement('a');
        link.href = '#' + heading.id;
        link.className = 'quick-jump-link';
        link.textContent = text;
        nav.appendChild(link);
        this.headings.push({ id: heading.id, element: heading, link: link });
      }, this);
    }

    onScroll() {
      var shouldShow = window.pageYOffset > this.threshold;
      if (shouldShow !== this.visible) {
        this.visible = shouldShow;
        this.menu.classList.toggle('qj--visible', shouldShow);
        if (!shouldShow) this.closeMenu();
      }
      this.updateActive();
    }

    updateActive() {
      var scrollPosition = window.pageYOffset + headerOffset() + 100;
      var current = null;
      this.headings.forEach(function (h) {
        var top = h.element.getBoundingClientRect().top + window.pageYOffset;
        if (scrollPosition >= top) current = h;
      });
      this.headings.forEach(function (h) {
        h.link.classList.toggle('qj--active', h === current);
      });
    }

    setupEvents() {
      var self = this;

      this.toggle.addEventListener('click', function (e) {
        e.stopPropagation();
        var expanded = self.toggle.getAttribute('aria-expanded') === 'true';
        self.toggleMenu(!expanded);
      });

      document.addEventListener('click', function (e) {
        if (!self.contains(e.target)) self.closeMenu();
      });

      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape') self.closeMenu();
      });

      this.nav.addEventListener('click', function (e) {
        var link = e.target.closest('a.quick-jump-link');
        if (!link) return;
        e.preventDefault();
        var target = document.getElementById(link.getAttribute('href').slice(1));
        if (!target) return;
        var top = target.getBoundingClientRect().top + window.pageYOffset - headerOffset() - 20;
        window.scrollTo({ top: top, behavior: 'smooth' });
        self.closeMenu();
      });
    }

    toggleMenu(show) {
      this.toggle.setAttribute('aria-expanded', show);
      this.content.classList.toggle('qj--open', show);
    }

    closeMenu() {
      this.toggleMenu(false);
    }
  }

  if (!customElements.get('quick-jump-menu')) {
    customElements.define('quick-jump-menu', QuickJumpMenu);
  }
})();
